import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { AdminLayout } from "./AdminLayout";
import { Card, CardContent } from "@/project/karuna/components/ui/card"; // @ts-ignore - card is not a valid module
import { Badge } from "@/project/karuna/components/ui/badge"; // @ts-ignore - badge is not a valid module
import { Button } from "@/project/karuna/components/ui/button"; // @ts-ignore - button is not a valid module
import { Input } from "@/project/karuna/components/ui/input"; // @ts-ignore - input is not a valid module
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/project/karuna/components/ui/select"; // @ts-ignore - select is not a valid module
import { Download, Search, Eye, Filter, Loader2 } from "lucide-react";
import { useToast } from "@/project/karuna/hooks/use-toast"; // @ts-ignore - use-toast is not a valid module
import { useKarunaLink } from "@/project/karuna/utils/navigationRoutes";
import { DynamicModuleService } from "@/services/APIs";
import { useQuery } from "@tanstack/react-query";
import {
  KarunaPagination,
  ListCardViewToggle,
  type KarunaViewMode,
} from "@/project/karuna/components/shared/KarunaListControls";
import { useKarunaPagedList } from "@/project/karuna/hooks/useKarunaPagedList";
import {
  mapWithConcurrency,
  DEFAULT_API_FETCH_CONCURRENCY,
} from "@/project/karuna/utils/mapWithConcurrency";

const SUBMISSION_SCHEMA_NAME = "Activity Submission";
const SCHOOL_PROFILE_SCHEMA_NAME = "School Profile";
const ACTIVITY_SCHEMA_NAME = "Activity Management";

// Normalize mobile number helper
const normalizeMobileNumber = (mobileNumber: string): string => {
  return String(mobileNumber ?? "")
    .replace(/\D/g, "")
    .slice(0, 10);
};

interface SubmissionRanking {
  id: string;
  rank: number;
  school_name: string;
  kc_no: string;
  activity_title: string;
  date: string;
  dateObj: Date | null; // Store original date for filtering
  score: number;
  status: string;
}

const AdminSubmissionReports = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const getLink = useKarunaLink();
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [timeFilter, _setTimeFilter] = useState("all");
  const [reportViewMode, setReportViewMode] = useState<KarunaViewMode>("list");

  // Fetch all submissions
  const { data: submissionsData, isLoading: submissionsLoading } = useQuery({
    queryKey: ["admin-submissions-rankings", SUBMISSION_SCHEMA_NAME],
    queryFn: async () => {
      try {
        const allSubmissions =
          await DynamicModuleService.getGridColItemsAllPages(
            SUBMISSION_SCHEMA_NAME
          );

        const submissionsWithFullData = await mapWithConcurrency(
          allSubmissions,
          DEFAULT_API_FETCH_CONCURRENCY,
          async (submission: any) => {
            const submissionId = submission.id ?? submission._id;
            if (!submissionId) {
              return submission;
            }

            try {
              const fullData = await DynamicModuleService.getItemById(
                SUBMISSION_SCHEMA_NAME,
                submissionId
              );
              const data = fullData?.data ?? fullData ?? submission;
              return { ...submission, ...data };
            } catch (err) {
              console.warn(
                `Could not fetch full data for submission ${submissionId}:`,
                err
              );
              return submission;
            }
          }
        );

        return submissionsWithFullData;
      } catch (error) {
        console.error("Error fetching submissions:", error);
        toast({
          title: "Error",
          description: "Failed to load submissions",
          variant: "destructive",
        });
        return [];
      }
    },
  });

  // Fetch school profiles
  const { data: schoolProfilesData } = useQuery({
    queryKey: ["school-profiles", SCHOOL_PROFILE_SCHEMA_NAME],
    queryFn: async () => {
      try {
        return await DynamicModuleService.getGridColItemsAllPages(
          SCHOOL_PROFILE_SCHEMA_NAME
        );
      } catch (error) {
        console.error("Error fetching school profiles:", error);
        return [];
      }
    },
  });

  // Fetch activities
  const { data: activitiesData } = useQuery({
    queryKey: ["activities", ACTIVITY_SCHEMA_NAME],
    queryFn: async () => {
      try {
        const activitiesList =
          await DynamicModuleService.getGridColItemsAllPages(
            ACTIVITY_SCHEMA_NAME
          );

        const activitiesWithFullData = await mapWithConcurrency(
          activitiesList,
          DEFAULT_API_FETCH_CONCURRENCY,
          async (activity: any) => {
            const activityId = activity.id ?? activity._id;
            if (!activityId) {
              return activity;
            }

            try {
              const fullData = await DynamicModuleService.getItemById(
                ACTIVITY_SCHEMA_NAME,
                activityId
              );
              const data = fullData?.data ?? fullData ?? activity;
              return { ...activity, ...data };
            } catch (err) {
              console.warn(
                `Could not fetch full data for activity ${activityId}:`,
                err
              );
              return activity;
            }
          }
        );

        return activitiesWithFullData;
      } catch (error) {
        console.error("Error fetching activities:", error);
        return [];
      }
    },
  });

  // Create activity map
  const activityMap = useMemo(() => {
    if (!activitiesData || !Array.isArray(activitiesData)) {
      return new Map<string, string>();
    }
    const map = new Map<string, string>();
    activitiesData.forEach((activity: any) => {
      const activityId = String(activity.id ?? activity._id ?? "").trim();
      const title =
        activity.title ??
        activity.activitytitle ??
        activity.activity_title ??
        "Unknown Activity";
      if (activityId) {
        map.set(activityId, title);
      }
    });
    return map;
  }, [activitiesData]);

  // Process and rank submissions
  const rankings = useMemo((): SubmissionRanking[] => {
    if (!submissionsData || !Array.isArray(submissionsData)) {
      return [];
    }

    // Process submissions
    const processed = submissionsData
      .map((submission: any): SubmissionRanking | null => {
        const submissionId = submission.id ?? submission._id ?? "";
        if (!submissionId) {
          return null;
        }

        // Get status (normalize to lowercase)
        const rawStatus = submission.status ?? submission.Status ?? "pending";
        const normalizedStatus = String(rawStatus).toLowerCase().trim();

        // Get score
        const score = submission.score ?? null;

        // Only filter out submissions without scores if we're showing approved status
        // For pending/rejected/all, we should show all submissions regardless of score
        if (
          statusFilter === "approved" &&
          (score === null || score === undefined)
        ) {
          return null; // Only include submissions with scores for approved filter
        }

        // For pending/rejected/all, include submissions even without scores
        // Use 0 as default score for ranking purposes if score is missing
        const scoreForRanking =
          score !== null && score !== undefined ? Number(score) : 0;

        // Get school contact for matching
        const submissionContact = normalizeMobileNumber(
          submission.schoolcontact ??
            submission.contact ??
            submission.school_contact ??
            submission.basicInfo?.schoolcontact ??
            ""
        );

        // Find matching school profile
        const schoolProfile = schoolProfilesData?.find((profile: any) => {
          const profileContact = normalizeMobileNumber(
            profile.contact ??
              profile.school_contact ??
              profile.schoolcontact ??
              ""
          );
          return profileContact === submissionContact && profileContact !== "";
        });

        // Get activity title
        const eventId = String(
          submission.event_id ?? submission.eventid ?? ""
        ).trim();
        let activityTitle = "Unknown Activity";
        if (eventId) {
          const mappedTitle = activityMap.get(eventId);
          if (mappedTitle) {
            activityTitle = mappedTitle;
          }
        }
        if (activityTitle === "Unknown Activity") {
          activityTitle =
            submission.activitytitle ??
            submission.activity_title ??
            submission.title ??
            "Unknown Activity";
        }

        // Get submission date
        const submittedAt =
          submission.submitted_at ??
          submission.created_on ??
          submission.created_at ??
          submission.submit_date ??
          "";

        // Parse date more carefully
        let dateObj: Date | null = null;
        if (submittedAt) {
          const parsed = new Date(submittedAt);
          if (!isNaN(parsed.getTime())) {
            dateObj = parsed;
          }
        }

        const date = dateObj
          ? (() => {
              // Format as DD-MM-YYYY
              const day = String(dateObj.getDate()).padStart(2, "0");
              const month = String(dateObj.getMonth() + 1).padStart(2, "0");
              const year = dateObj.getFullYear();
              return `${day}-${month}-${year}`;
            })()
          : "N/A";

        return {
          id: submissionId,
          rank: 0, // Will be set after sorting
          school_name:
            schoolProfile?.schoolname ??
            submission.schoolname ??
            "Unknown School",
          kc_no: schoolProfile?.kcno ?? submission.kcno ?? "N/A",
          activity_title: activityTitle,
          date: date,
          dateObj: dateObj,
          score: scoreForRanking,
          status: normalizedStatus,
        };
      })
      .filter((s): s is SubmissionRanking => s !== null)
      .filter(s => {
        // Apply status filter
        if (statusFilter !== "all" && s.status !== statusFilter) {
          return false;
        }

        // Apply time filter
        if (timeFilter !== "all") {
          // If no date, exclude from filtered results
          if (!s.dateObj) {
            return false;
          }

          // Create a copy of the submission date to avoid mutating the original
          const submissionDate = new Date(s.dateObj.getTime());
          submissionDate.setHours(0, 0, 0, 0);

          // Get today's date at start of day
          const today = new Date();
          today.setHours(0, 0, 0, 0);

          // Calculate start date based on filter
          const startDate = new Date(today);
          startDate.setHours(0, 0, 0, 0);

          switch (timeFilter) {
            case "week":
              startDate.setDate(startDate.getDate() - 7);
              break;
            case "month":
              startDate.setMonth(startDate.getMonth() - 1);
              break;
            case "quarter":
              startDate.setMonth(startDate.getMonth() - 3);
              break;
            case "year":
              startDate.setFullYear(startDate.getFullYear() - 1);
              break;
            default:
              return true; // If filter value is invalid, show all
          }

          // Filter: submission date must be >= startDate and <= today
          // Only include submissions within the time range
          const isInRange =
            submissionDate >= startDate && submissionDate <= today;
          if (!isInRange) {
            return false;
          }
        }

        // Apply search filter
        if (searchQuery) {
          const query = searchQuery.toLowerCase();
          return (
            s.school_name.toLowerCase().includes(query) ||
            s.kc_no.toLowerCase().includes(query) ||
            s.activity_title.toLowerCase().includes(query)
          );
        }

        return true;
      })
      .sort((a, b) => {
        // Sort by score descending, but put submissions with score 0 (no score) at the end
        if (a.score === 0 && b.score !== 0) {
          return 1;
        }
        if (a.score !== 0 && b.score === 0) {
          return -1;
        }
        return b.score - a.score;
      })
      .map((submission, index) => ({
        ...submission,
        rank: index + 1,
      }));

    return processed;
  }, [
    submissionsData,
    schoolProfilesData,
    activityMap,
    statusFilter,
    timeFilter,
    searchQuery,
  ]);

  // Calculate stats
  const stats = useMemo(() => {
    const total = rankings.length;
    const approved = rankings.filter(s => s.status === "approved").length;
    const pending = rankings.filter(s => s.status === "pending").length;
    const rejected = rankings.filter(s => s.status === "rejected").length;

    // Calculate average score only from submissions that have scores (score > 0)
    const submissionsWithScores = rankings.filter(s => s.score > 0);
    const averageScore =
      submissionsWithScores.length > 0
        ? Math.round(
            submissionsWithScores.reduce((sum, s) => sum + s.score, 0) /
              submissionsWithScores.length
          )
        : 0;

    // Top score only from submissions that have scores
    const topScore =
      submissionsWithScores.length > 0
        ? Math.max(...submissionsWithScores.map(s => s.score))
        : 0;

    // Get the count for the current status filter
    const getFilteredCount = () => {
      if (statusFilter === "approved") {
        return approved;
      }
      if (statusFilter === "pending") {
        return pending;
      }
      if (statusFilter === "rejected") {
        return rejected;
      }
      return total; // "all" shows total
    };

    return {
      total,
      approved,
      pending,
      rejected,
      filteredCount: getFilteredCount(),
      averageScore,
      topScore,
    };
  }, [rankings, statusFilter]);

  const {
    page: reportPage,
    setPage: setReportPage,
    totalPages: reportTotalPages,
    paginatedItems: pagedRankings,
    totalItems: reportFilteredTotal,
    pageSize: reportPageSize,
    setPageSize: setReportPageSize,
  } = useKarunaPagedList(rankings, searchQuery, statusFilter, timeFilter);

  const getRankBadgeColor = (rank: number) => {
    if (rank === 1) {
      return "bg-yellow-500 text-white";
    }
    if (rank === 2) {
      return "bg-gray-400 text-white";
    }
    if (rank === 3) {
      return "bg-amber-700 text-white";
    }
    return "bg-gray-200 text-gray-700";
  };

  const handleExportPDF = async () => {
    try {
      // Dynamically import jsPDF to avoid SSR issues
      const jsPDFModule = await import("jspdf");
      const jsPDF = jsPDFModule.default;
      const autoTableModule = await import("jspdf-autotable");
      const autoTable = autoTableModule.default;

      const doc = new jsPDF("p", "mm", "a4");
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 20; // Increased margin to center smaller table
      let yPosition = margin;

      // Get status-based heading
      const getStatusHeading = () => {
        if (statusFilter === "approved") {
          return "Approved Submissions";
        } else if (statusFilter === "rejected") {
          return "Rejected Submissions";
        } else if (statusFilter === "pending") {
          return "Pending Submissions";
        } else {
          return "All Submissions";
        }
      };

      // Main Title - Large, bold, centered
      doc.setFontSize(24);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(0, 0, 0);
      doc.text("Submission Rankings Report", pageWidth / 2, yPosition, {
        align: "center",
      });
      yPosition += 10;

      // Status-based heading - Medium, bold, centered
      doc.setFontSize(18);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(0, 0, 0);
      doc.text(getStatusHeading(), pageWidth / 2, yPosition, {
        align: "center",
      });
      yPosition += 15;

      // Table data - Rank, School, Activity, Date, Score
      const tableData = rankings.map(ranking => [
        ranking.rank.toString(),
        `${ranking.school_name} (${ranking.kc_no})`,
        ranking.activity_title,
        ranking.date,
        ranking.score > 0 ? ranking.score.toString() : "N/A",
      ]);

      // Calculate table width (smaller, centered)
      const tableWidth = pageWidth - margin * 2;

      // Generate table
      autoTable(doc, {
        head: [["Rank", "School", "Activity", "Date", "Score"]],
        body: tableData,
        startY: yPosition,
        styles: {
          fontSize: 9,
          cellPadding: 4,
          overflow: "linebreak",
          lineColor: [200, 200, 200],
          lineWidth: 0.5,
        },
        headStyles: {
          fillColor: [70, 70, 70],
          textColor: [255, 255, 255],
          fontStyle: "bold",
          fontSize: 10,
          halign: "left",
          valign: "middle",
          cellPadding: 4,
        },
        columnStyles: {
          0: {
            cellWidth: 20,
            halign: "center",
            fontStyle: "bold",
            textColor: [0, 0, 0],
          },
          1: {
            cellWidth: 45,
            halign: "left",
            textColor: [0, 0, 0],
          },
          2: {
            cellWidth: 55,
            halign: "left",
            textColor: [0, 0, 0],
          },
          3: {
            cellWidth: 25,
            halign: "center",
            textColor: [0, 0, 0],
          },
          4: {
            cellWidth: 20,
            halign: "center",
            fontStyle: "bold",
            textColor: [0, 0, 0],
          },
        },
        bodyStyles: {
          textColor: [0, 0, 0],
          fillColor: [255, 255, 255],
          cellPadding: 4,
        },
        alternateRowStyles: {
          fillColor: [245, 245, 245],
        },
        margin: { top: yPosition, left: margin, right: margin },
        tableWidth: tableWidth,
        theme: "striped",
      });

      // Add page numbers
      const totalPages = doc.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        doc.setFontSize(9);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(150, 150, 150);
        doc.text(
          `Page ${i} of ${totalPages}`,
          pageWidth - margin,
          pageHeight - 10,
          { align: "right" }
        );
      }

      // Generate filename with current date and status
      const now = new Date();
      const dateStr = now.toISOString().split("T")[0];
      const statusStr =
        statusFilter === "all"
          ? "All"
          : statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1);
      const filename = `Submission_Rankings_${statusStr}_${dateStr}.pdf`;

      // Save PDF
      doc.save(filename);

      toast({
        title: "Success",
        description: "PDF report downloaded successfully",
      });
    } catch (error) {
      console.error("Error generating PDF:", error);
      toast({
        title: "Error",
        description: "Failed to generate PDF report",
        variant: "destructive",
      });
    }
  };

  const loading = submissionsLoading;

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground">
              Submission Rankings Report
            </h1>
            <p className="mt-1 text-muted-foreground">
              View school submissions ranked by score
            </p>
          </div>
          <Button
            variant="outline"
            className="gap-2"
            onClick={handleExportPDF}
            disabled={loading || rankings.length === 0}
          >
            <Download className="h-4 w-4" />
            Export Report
          </Button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6">
          <Card className="border-border/50 shadow-medium">
            <CardContent className="p-6">
              <div>
                <h3 className="mb-2 text-sm font-medium text-muted-foreground">
                  Total Submissions
                </h3>
                <p className="text-3xl font-bold text-foreground">
                  {stats.total}
                </p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-border/50 shadow-medium">
            <CardContent className="p-6">
              <div>
                <h3 className="mb-2 text-sm font-medium text-muted-foreground">
                  Approved
                </h3>
                <p className="text-3xl font-bold text-green-600">
                  {stats.approved}
                </p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-border/50 shadow-medium">
            <CardContent className="p-6">
              <div>
                <h3 className="mb-2 text-sm font-medium text-muted-foreground">
                  Pending
                </h3>
                <p className="text-3xl font-bold text-yellow-600">
                  {stats.pending}
                </p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-border/50 shadow-medium">
            <CardContent className="p-6">
              <div>
                <h3 className="mb-2 text-sm font-medium text-muted-foreground">
                  Rejected
                </h3>
                <p className="text-3xl font-bold text-red-600">
                  {stats.rejected}
                </p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-border/50 shadow-medium">
            <CardContent className="p-6">
              <div>
                <h3 className="mb-2 text-sm font-medium text-muted-foreground">
                  Average Score
                </h3>
                <p className="text-3xl font-bold text-foreground">
                  {stats.averageScore}
                </p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-border/50 shadow-medium">
            <CardContent className="p-6">
              <div>
                <h3 className="mb-2 text-sm font-medium text-muted-foreground">
                  Top Score
                </h3>
                <p className="text-3xl font-bold text-yellow-600">
                  {stats.topScore}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters Section */}
        <Card className="border-border/50 bg-muted/30">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">Filters</span>
              </div>
              <div className="min-w-[200px] flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    placeholder="Search school, KC no, event.."
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    className="pl-9"
                  />
                </div>
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Rankings Table */}
        {loading ? (
          <div className="flex h-64 items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : rankings.length > 0 ? (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <ListCardViewToggle
                viewMode={reportViewMode}
                onViewChange={setReportViewMode}
              />
            </div>

            <Card className="border-border/50 shadow-medium">
              <CardContent className="p-0">
                {reportViewMode === "list" ? (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-muted/50">
                        <tr>
                          <th className="px-6 py-3 text-left text-sm font-semibold text-muted-foreground">
                            Rank
                          </th>
                          <th className="px-6 py-3 text-left text-sm font-semibold text-muted-foreground">
                            School
                          </th>
                          <th className="px-6 py-3 text-left text-sm font-semibold text-muted-foreground">
                            Activity
                          </th>
                          <th className="px-6 py-3 text-left text-sm font-semibold text-muted-foreground">
                            Date
                          </th>
                          <th className="px-6 py-3 text-left text-sm font-semibold text-muted-foreground">
                            Score
                          </th>
                          <th className="px-6 py-3 text-left text-sm font-semibold text-muted-foreground">
                            Status
                          </th>
                          <th className="px-6 py-3 text-left text-sm font-semibold text-muted-foreground">
                            Action
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {pagedRankings.map(ranking => (
                          <tr
                            key={ranking.id}
                            className="transition-colors hover:bg-muted/30"
                          >
                            <td className="px-6 py-4">
                              <div
                                className={`inline-flex h-8 w-8 items-center justify-center rounded-full text-sm font-bold ${getRankBadgeColor(
                                  ranking.rank
                                )}`}
                              >
                                {ranking.rank}
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <div>
                                <p className="font-medium">
                                  {ranking.school_name}
                                </p>
                                <p className="text-sm text-muted-foreground">
                                  {ranking.kc_no}
                                </p>
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <p className="font-medium">
                                {ranking.activity_title}
                              </p>
                            </td>
                            <td className="px-6 py-4">
                              <p className="text-muted-foreground">
                                {ranking.date}
                              </p>
                            </td>
                            <td className="px-6 py-4">
                              <p className="font-bold text-blue-600">
                                {ranking.score > 0
                                  ? `${ranking.score}/100`
                                  : "N/A"}
                              </p>
                            </td>
                            <td className="px-6 py-4">
                              <Badge
                                className={
                                  ranking.status === "approved"
                                    ? "bg-green-500 text-white"
                                    : ranking.status === "pending"
                                      ? "bg-yellow-500 text-white"
                                      : "bg-red-500 text-white"
                                }
                              >
                                {ranking.status.charAt(0).toUpperCase() +
                                  ranking.status.slice(1)}
                              </Badge>
                            </td>
                            <td className="px-6 py-4">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() =>
                                  navigate(
                                    getLink(`/admin/submissions/${ranking.id}`)
                                  )
                                }
                                className="gap-2"
                              >
                                <Eye className="h-4 w-4" />
                                View
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="grid gap-4 p-4 md:grid-cols-2 lg:grid-cols-3">
                    {pagedRankings.map(ranking => (
                      <Card
                        key={ranking.id}
                        className="border border-border shadow-sm"
                      >
                        <CardContent className="space-y-3 p-4">
                          <div className="flex items-center justify-between gap-2">
                            <div
                              className={`inline-flex h-9 w-9 items-center justify-center rounded-full text-sm font-bold ${getRankBadgeColor(
                                ranking.rank
                              )}`}
                            >
                              #{ranking.rank}
                            </div>
                            <Badge
                              className={
                                ranking.status === "approved"
                                  ? "bg-green-500 text-white"
                                  : ranking.status === "pending"
                                    ? "bg-yellow-500 text-white"
                                    : "bg-red-500 text-white"
                              }
                            >
                              {ranking.status.charAt(0).toUpperCase() +
                                ranking.status.slice(1)}
                            </Badge>
                          </div>
                          <div>
                            <p className="font-semibold">
                              {ranking.school_name}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {ranking.kc_no}
                            </p>
                          </div>
                          <p className="line-clamp-2 text-sm">
                            {ranking.activity_title}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {ranking.date}
                          </p>
                          <p className="font-bold text-blue-600">
                            {ranking.score > 0 ? `${ranking.score}/100` : "N/A"}
                          </p>
                          <Button
                            variant="outline"
                            size="sm"
                            className="w-full gap-2"
                            onClick={() =>
                              navigate(
                                getLink(`/admin/submissions/${ranking.id}`)
                              )
                            }
                          >
                            <Eye className="h-4 w-4" />
                            View
                          </Button>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            <KarunaPagination
              page={reportPage}
              totalPages={reportTotalPages}
              totalItems={reportFilteredTotal}
              pageSize={reportPageSize}
              onPageChange={setReportPage}
              onPageSizeChange={setReportPageSize}
            />
          </div>
        ) : (
          <Card>
            <CardContent className="p-12 text-center">
              <p className="text-muted-foreground">
                No submissions found matching the selected filters.
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminSubmissionReports;
